"use client";

import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getApiBaseUrl } from "@/lib/api-base-url";

const API_BASE =
  getApiBaseUrl();

const inputClass =
  "h-10 w-full rounded-[10px] border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20";

type Props = {
  courseSlug: string;
  planSlug: string;
  className?: string;
};

export function CohortWaitlistForm({ courseSlug, planSlug, className }: Props) {
  const t = useTranslations("courseDetail.seats");
  const locale = useLocale() as "en" | "bn";
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const source = `cohort-waitlist:${courseSlug}:${planSlug}`.toLowerCase();
      const res = await fetch(`${API_BASE}/v1/interest-list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim(),
          ...(name.trim() ? { name: name.trim() } : {}),
          source,
          locale,
          meta: { courseSlug, planSlug },
        }),
      });
      const raw = (await res.json()) as { success?: boolean; error?: { code?: string } };
      if (!res.ok || !raw.success) {
        setError(raw.error?.code === "RATE_LIMITED" ? t("rateLimited") : t("error"));
        return;
      }
      setSuccess(true);
    } catch {
      setError(t("error"));
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <p className={cn("text-center text-sm font-medium text-emerald-700", className)}>
        {t("success")}
      </p>
    );
  }

  if (!open) {
    return (
      <Button
        type="button"
        variant="secondary"
        className={cn("w-full rounded-[10px] font-semibold", className)}
        onClick={() => {
          setOpen(true);
          setError(null);
        }}
      >
        {t("waitlistCta")}
      </Button>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className={cn("space-y-3 rounded-xl border border-neutral-200 bg-neutral-50 p-3", className)}
    >
      <p className="text-xs leading-5 text-neutral-500">{t("waitlistHint")}</p>
      <input
        type="email"
        required
        autoComplete="email"
        placeholder={t("emailPlaceholder")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={inputClass}
      />
      <input
        type="text"
        autoComplete="name"
        placeholder={t("namePlaceholder")}
        value={name}
        onChange={(e) => setName(e.target.value)}
        className={inputClass}
      />
      {error && (
        <p className="flex items-center gap-1.5 text-xs text-red-600">
          <AlertCircle aria-hidden className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
      <Button type="submit" disabled={loading} className="w-full rounded-[10px]">
        {loading ? <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" /> : null}
        {t("submit")}
      </Button>
    </form>
  );
}
